// Ein vergrößertes Foto im CRM zoomen und verschieben (O88; Gernot,
// Augentest E3b, 23.10.2026: „im Büro will man Typenschilder lesen“) – die
// reine Rechnung, ohne React. Gezoomt wird das Foto samt Zeichnung: beide
// liegen in einer Ebene, die als Ganzes verschoben und vergrößert wird
// (CSS: translate(x, y) scale(s), Ursprung in der Mitte).
//
// Ein Punkt, der in der Ebene um q von der Mitte liegt, steht auf dem
// Bildschirm bei x + s·q. Beim Zoomen bleibt der Punkt unter der Maus (oder
// zwischen den Fingern) stehen; beim Verschieben bleibt immer Foto im Feld –
// kein Rand ins Leere.

/** Zoom und Verschiebung: s ≥ 1, x und y in Bildschirm-Pixeln von der Mitte. */
export interface ZoomStand {
  s: number;
  x: number;
  y: number;
}

export const OHNE_ZOOM: ZoomStand = { s: 1, x: 0, y: 0 };
/** Weiter als sechsfach bringt nichts – die Fotos sind verkleinert (CLAUDE.md, „Wo sie liegen“). */
export const ZOOM_HOECHSTENS = 6;
/** Ein Schritt mit den Knöpfen „+“ und „−“ (und den Tasten). */
export const ZOOM_SCHRITT = 1.5;
/** Ein Doppelklick oder Doppeltipp springt auf diese Stufe – oder zurück. */
export const ZOOM_DOPPEL = 2.5;

/** Die Größe des Felds, in dem das Foto liegt. */
export interface Feld {
  breite: number;
  hoehe: number;
}

/** Nicht kleiner als das Feld, nicht größer als ZOOM_HOECHSTENS – und kein Rand ins Leere. */
export function begrenzt(z: ZoomStand, feld: Feld): ZoomStand {
  const s = Math.min(ZOOM_HOECHSTENS, Math.max(1, z.s));
  const randX = ((s - 1) * feld.breite) / 2;
  const randY = ((s - 1) * feld.hoehe) / 2;
  const klemmen = (wert: number, rand: number) => Math.min(rand, Math.max(-rand, wert)) || 0;
  return { s, x: klemmen(z.x, randX), y: klemmen(z.y, randY) };
}

/**
 * Um den Faktor zoomen – der Punkt `um` (Bildschirm-Pixel von der Mitte des
 * Felds) bleibt, wo er ist. Ohne `um`: um die Mitte.
 */
export function zoomen(z: ZoomStand, faktor: number, feld: Feld, um: { x: number; y: number } = { x: 0, y: 0 }): ZoomStand {
  const s = Math.min(ZOOM_HOECHSTENS, Math.max(1, z.s * faktor));
  const wirklich = s / z.s;
  return begrenzt({ s, x: um.x - wirklich * (um.x - z.x), y: um.y - wirklich * (um.y - z.y) }, feld);
}

/** Ziehen: um dx, dy verschieben – nur, soweit Foto da ist. */
export function verschieben(z: ZoomStand, dx: number, dy: number, feld: Feld): ZoomStand {
  return begrenzt({ s: z.s, x: z.x + dx, y: z.y + dy }, feld);
}

/** Das Mausrad: ein Raster nach oben (deltaY < 0) vergrößert – sanft, auch mit dem Touchpad. */
export function radFaktor(deltaY: number): number {
  return Math.exp(-Math.max(-100, Math.min(100, deltaY)) * 0.0025);
}

/** Doppelklick: ungezoomt auf ZOOM_DOPPEL an dieser Stelle, sonst zurück. */
export function doppelt(z: ZoomStand, feld: Feld, um: { x: number; y: number }): ZoomStand {
  return z.s > 1.01 ? OHNE_ZOOM : zoomen(z, ZOOM_DOPPEL / z.s, feld, um);
}

/** Die CSS-Verwandlung der Ebene. */
export function zoomStil(z: ZoomStand): string {
  return `translate(${z.x.toFixed(1)}px, ${z.y.toFixed(1)}px) scale(${z.s.toFixed(4)})`;
}

/** „250 %“ – für die Anzeige neben „+“ und „−“. */
export function zoomProzent(z: ZoomStand): string {
  return `${Math.round(z.s * 100)} %`;
}
